import * as React from "react";
import { useSearchParams, useNavigate, Link } from "react-router";
import { apiFetch, APIError, NetworkError } from "@/lib/api-client";
import { parseJWTClaims } from "@/lib/jwt-claims";
import { useAuth } from "./auth-context";
import { toast } from "@/components/ui/toaster";

/**
 * Verify-Route — Schritt 2 des magic-link-flows.
 *
 * Der mail-link führt zu /auth/verify?token=<raw>. Auf der landing-domain
 * (hitzeprotokoll.de) liefert das backend eine confirm-HTML-page; auf
 * der app-domain (app.hitzeprotokoll.de) hat WIR die kontrolle und
 * machen direkt-JSON-POST — das umgeht mail-prefetcher-token-verbrauch
 * (siehe ADR-002 § Magic-Link, two-step pattern).
 *
 * Bei success: JWT-pair in dexie speichern → AuthProvider switched auf
 * "authenticated" → AuthGate redirected automatisch zu /dashboard.
 */

interface VerifyResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
}

export function VerifyRoute(): React.ReactElement {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const token = params.get("token") ?? "";
  const [error, setError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const fired = React.useRef(false);

  // Auto-fire wenn token im query-param. Manual-button-fallback unten
  // für scanners die uns prefetcht haben (token bereits consumed).
  const performVerify = React.useCallback(async () => {
    if (!token) {
      setError("invalid_or_missing_token");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const resp = await apiFetch<VerifyResponse>("/auth/verify", {
        method: "POST",
        anonymous: true,
        body: { token },
      });
      const claims = parseJWTClaims(resp.access_token);
      await auth.login({
        id: "current",
        accessToken: resp.access_token,
        refreshToken: resp.refresh_token,
        accessTokenExpiresAt: Date.now() + resp.expires_in * 1000,
        user: {
          userId: claims.sub ?? "",
          orgId: claims.org_id ?? "",
          role:
            (claims.role as
              | "admin"
              | "leitung"
              | "betreuer"
              | "traeger_admin") ?? "betreuer",
          email: claims.email,
          parentOrgId: claims.parent_org_id,
          licenseExpiresAt: claims.lic_exp,
        },
        loggedInAt: Date.now(),
      });
      // §4d: Erfolgs-Bestätigung — schließt den Loop und bestätigt die lange Session
      toast("Angemeldet. Sie bleiben auf diesem Gerät angemeldet.", "info", 4000);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      if (err instanceof NetworkError) {
        setError("network_error");
      } else if (err instanceof APIError) {
        setError(err.code);
      } else {
        setError("unknown");
      }
      setSubmitting(false);
    }
  }, [token, auth, navigate]);

  React.useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    void performVerify();
  }, [performVerify]);

  if (submitting) {
    return (
      <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-4 px-6 text-center">
        <div
          role="status"
          aria-label="Anmeldung wird geprüft"
          className="size-10 animate-spin rounded-full border-4 border-[color:var(--color-border)] border-t-[color:var(--color-brand-primary-strong)]"
        />
        <p className="text-sm text-neutral-600">Anmeldung wird geprüft …</p>
      </main>
    );
  }

  return (
    <main className="mx-auto flex min-h-screen max-w-md flex-col justify-center gap-6 px-6 py-10">
      <header className="space-y-2">
        <h1 className="text-3xl font-semibold">Anmeldung fehlgeschlagen</h1>
        <p className="text-neutral-600">{describeError(error)}</p>
      </header>
      <Link
        to="/login"
        className="rounded-md bg-[color:var(--color-brand-primary-strong)] px-6 py-3 text-center font-semibold text-white"
      >
        Neuen Anmelde-Link anfordern
      </Link>
    </main>
  );
}

function describeError(code: string | null): string {
  switch (code) {
    case "invalid_or_missing_token":
    case "invalid_or_expired_token":
      return "Der Anmelde-Link ist ungültig oder bereits abgelaufen.";
    case "invalid_or_consumed_token":
      return "Dieser Anmelde-Link wurde bereits verwendet.";
    case "network_error":
      return "Keine Verbindung zum Server. Bitte Internetverbindung prüfen.";
    case "rate_limited":
      return "Zu viele Versuche. Bitte einige Minuten warten.";
    default:
      return "Es ist ein Fehler aufgetreten. Bitte erneut versuchen.";
  }
}
